import {
  addToast,
  Button,
  DateInput,
  Select,
  TextInput,
  useMobile,
  SelectOptionType,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import {
  deactivateTeacherAccount,
  sendTeacherAccountActivationLink,
  sendTeacherPasswordRecoveryLink,
  updateAccount,
} from '../../../../api/requests/teacherActivation';
import { PathsLayouts } from '../../../../config/routes';
import { useParameters } from '../../../../hooks/useParameters';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { IdentificationDocumentCode } from '../../../../utils/enums/identification-document-code.enum';
import { getSelectedOption } from '../../../../utils/selectFormat';
import { useIdentificationTypeLoader } from '../../../../components/loaders/IdentificationTypeLoader';
import FormDataLoader from '../loaders/FormDataLoader';
import { teacherRolesOptions } from '../types';
import ConfirmModal, { ConfirmModalRef } from './ConfirmModal';
import DeactivateConfirmModal from './DeactivateConfirmModal';
import EmailConfirmModal from './EmailConfirmModal';
import ExistingAccountConfirmModal from './ExistingAccountConfirmModal';

type FormValues = {
  identificationTypeId: string;
  countryId?: string | null;
  identification: string;
  names: string;
  paternalLastName: string;
  maternalLastName: string;
  email: string;
  personalEmail: string;
  birthday: string;
  campus: SelectOptionType | null;
  school: SelectOptionType | null;
  teacherRole: SelectOptionType | null;
  accountId?: number;
};

const DEFAULT_VALUES: FormValues = {
  identificationTypeId: '',
  countryId: null,
  identification: '',
  names: '',
  paternalLastName: '',
  maternalLastName: '',
  email: '',
  personalEmail: '',
  birthday: '',
  campus: null,
  school: null,
  teacherRole: null,
  accountId: undefined,
};

enum ModalType {
  NONE,
  EMAIL_CONFIRM,
  DEACTIVATE_CONFIRM,
  EXISTING_ACCOUNT_CONFIRM,
}

enum SubmitAction {
  ACTIVATING,
  PWD_RECOVERY,
  DEACTIVATING,
  UPDATE_DATA,
}

interface Props {
  canDeactivateAccount?: boolean;
  canActivateAccount?: boolean;
  canRecoverPassword?: boolean;
  initialValues?: Partial<FormValues>;
  enableAllFields?: boolean;
  onEmailChangeSuccess?: (newEmail: string) => void;
  onDeactivationSuccess?: () => void;
}

export default function TeacherDataForm({
  initialValues,
  canDeactivateAccount = false,
  canActivateAccount = false,
  canRecoverPassword = false,
  enableAllFields = false,
  onEmailChangeSuccess = () => null,
  onDeactivationSuccess = () => null,
}: Props) {
  const isMobile = useMobile();
  const { t } = useTranslation(
    TranslationsKeys.TEACHER_MANAGMENT_TEACHER_ACTIVATION,
  );
  const prefix = 'teacherForm';
  const history = useHistory();
  const {
    validateTextNotEmpty,
    validateRut,
    msgValidations,
  } = useValidations();
  const {
    handleSubmit,
    control,
    getValues,
    reset,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { ...DEFAULT_VALUES, ...initialValues },
  });
  const { countryOptions } = useParameters();
  const { data } = useIdentificationTypeLoader();

  const [openedModal, setOpenedModal] = useState<ModalType>(ModalType.NONE);
  const [executingAction, setExecutingAction] = useState<SubmitAction>();
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  /* Usado para guardar el email introducido previamente en la confirm de cuenta ya existente */
  const savedEmailForConfirmation = useRef<string>();
  const educationalBackgroundModal = useRef<ConfirmModalRef>(null);

  const identificationTypeSelect = useMemo(() => {
    return (data?.identificationTypes ?? []).map((identificationType) => ({
      value: identificationType.id,
      label: identificationType.name,
      rules: identificationType.rules,
    }));
  }, [data]);

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const {
    identification,
    email,
    identificationTypeId: identificationType,
    countryId: country,
    teacherRole,
  } = watch();

  useEffect(() => {
    const newValue = identification
      ?.replace('.', '')
      .replace('-', '')
      .toUpperCase();
    setValue('identification', newValue);
  }, [setValue, identification]);

  const selectedIdentificationType = useMemo(() => {
    return identificationTypeSelect.find(
      (it) => it.value === identificationType,
    );
  }, [identificationTypeSelect, identificationType]);

  const dynamicValidate = useCallback(
    (value: string | number | SelectOptionType | null | undefined) => {
      if (!value || typeof value !== 'string') {
        return undefined;
      }
      if (
        selectedIdentificationType?.value ===
        IdentificationDocumentCode.Passport
      ) {
        return undefined;
      }
      if (
        selectedIdentificationType?.value === IdentificationDocumentCode.CL_RUT
      ) {
        return validateRut(value);
      }
      if (selectedIdentificationType?.rules) {
        const rules = selectedIdentificationType.rules as {
          replacement?: Record<string, string>;
          min?: number;
          max?: number;
        };
        if (rules.replacement) {
          const patterns = Object.values(rules.replacement);
          const charPattern =
            patterns.length === 1 ? patterns[0] : `(?:${patterns.join('|')})`;
          const min = rules.min ?? 1;
          const max = rules.max ?? 20;
          const regex = new RegExp(`^${charPattern}{${min},${max}}$`);
          return regex.test(value) ? undefined : 'Formato inválido';
        }
      }

      return undefined;
    },
    [selectedIdentificationType, validateRut],
  );

  const sendInvitationLink = async (data: FormValues, force?: boolean) => {
    setIsLoadingRequest(true);

    const { error, data: response } = await sendTeacherAccountActivationLink({
      identification: data.identification,
      identificationTypeId: data.identificationTypeId,
      countryId: data?.countryId,
      email: data.email,
      personalEmail: data.personalEmail,
      name: data.names,
      maternalLastName: data.maternalLastName,
      paternalLastName: data.paternalLastName,
      birthday: data.birthday,
      ignoreWarnings: force,
      teacherRoleId: data.teacherRole?.value ?? '',
      campusId: data.campus?.value ?? '',
      schoolId: data.school?.value ?? '',
    });

    setIsLoadingRequest(false);

    if (error) {
      if (typeof error.data.message === 'object' && error.data.message.length) {
        // Si la cuenta ya existia
        savedEmailForConfirmation.current = data.email;
        setOpenedModal(ModalType.EXISTING_ACCOUNT_CONFIRM);
      } else if (error.data.message === 'Email Duplicate') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.duplicatedEmail`),
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.errorSendingInvitation`),
        });
      }
      return;
    }

    if (force) {
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.grantedPermissions`),
      });
    } else {
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.invitationSent`),
      });
    }
    setOpenedModal(ModalType.NONE);
    onEmailChangeSuccess(data.email);
    if (response?.teacherId) {
      educationalBackgroundModal.current?.openModal(response?.teacherId);
    }
  };

  const sendResetPasswordLink = async (
    data: Pick<
      FormValues,
      'email' | 'identification' | 'identificationTypeId' | 'countryId'
    >,
  ) => {
    setIsLoadingRequest(true);
    const { error } = await sendTeacherPasswordRecoveryLink({
      ...data,
      identification: data.identification,
      identificationTypeId: data.identificationTypeId,
      countryId: data?.countryId,
    });

    setIsLoadingRequest(false);

    if (error) {
      if (error.data.message === 'Email Duplicate') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.duplicatedEmail`),
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.errorSendingRecoveryPasswordEmail`),
        });
      }
      return;
    }

    addToast({
      icon: 'success',
      color: 'success',
      text: t(`${prefix}.recoveryPasswordEmailSent`),
    });

    setOpenedModal(ModalType.NONE);
    onEmailChangeSuccess(data.email);
  };

  const deactivateAccount = async (
    data: Pick<
      FormValues,
      'email' | 'identification' | 'identificationTypeId' | 'countryId'
    >,
  ) => {
    setIsLoadingRequest(true);
    const { error } = await deactivateTeacherAccount({
      ...data,
      identification: data.identification,
      identificationTypeId: data.identificationTypeId,
      countryId: data?.countryId,
    });

    setIsLoadingRequest(false);

    if (error) {
      return addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${prefix}.failedDeactivatingTeacherAccount`),
      });
    }

    addToast({
      icon: 'success',
      color: 'success',
      text: t(`${prefix}.deactivatedTeacherAccount`),
    });

    setOpenedModal(ModalType.NONE);
    onDeactivationSuccess();
  };

  const onUpdateData = async (data: FormValues, force?: boolean) => {
    if (!data.accountId) {
      return;
    }

    setIsLoadingRequest(true);
    const { error, data: response } = await updateAccount({
      accountId: data.accountId,
      email: data.email,
      ignoreWarnings: force,
      teacherRoleId: data.teacherRole?.value ?? '',
      campusId: data.campus?.value ?? '',
      schoolId: data.school?.value ?? '',
    });
    setIsLoadingRequest(false);

    if (error) {
      if (typeof error.data.message === 'object' && error.data.message.length) {
        // Si la cuenta ya existia
        savedEmailForConfirmation.current = data.email;
        setOpenedModal(ModalType.EXISTING_ACCOUNT_CONFIRM);
      } else if (error.data.message === 'Email Duplicate') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.duplicatedEmail`),
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.errorSendingInvitation`),
        });
      }
      return;
    }

    if (force) {
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.updateDataSuccess`),
      });
    } else if (response?.teacherId) {
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.updateDataSuccess`),
      });
    }
    setOpenedModal(ModalType.NONE);
  };

  /* 
    Todos los botones primarios del formulario ejecutan el submit, por eso se guarda cual fue la accion
    que queria ejecturase y se toman acciones segun ese valor
  */
  const onSubmit = () => {
    switch (executingAction) {
      case SubmitAction.DEACTIVATING:
        setOpenedModal(ModalType.DEACTIVATE_CONFIRM);
        break;
      case SubmitAction.UPDATE_DATA:
        onUpdateData(getValues());
        break;
      default:
        setOpenedModal(ModalType.EMAIL_CONFIRM);
        break;
    }
  };

  const handleEmailConfirmation = (confirmedEmail: string, force?: boolean) => {
    const data = { ...getValues(), email: confirmedEmail };
    switch (executingAction) {
      case SubmitAction.ACTIVATING: {
        sendInvitationLink(data, force);
        break;
      }
      case SubmitAction.PWD_RECOVERY: {
        sendResetPasswordLink({
          email: data.email,
          identification: data.identification,
          identificationTypeId: data.identificationTypeId,
          countryId: data?.countryId,
        });
        break;
      }
      case SubmitAction.UPDATE_DATA:
        onUpdateData(data, force);
        break;
    }
  };

  const handleCancel = () => {
    setOpenedModal(ModalType.NONE);
    savedEmailForConfirmation.current = undefined;
  };

  const goToPersonalBackground = useCallback(
    (techerId: number) => {
      history.push(
        `${PathsLayouts.teacherFile}/${techerId}/personal-background`,
      );
    },
    [history],
  );

  return (
    <FormDataLoader
      children={({ data: { campusesOptions, schoolsOptions } }) => (
        <>
          <EmailConfirmModal
            initialEmail={email}
            isOpen={openedModal === ModalType.EMAIL_CONFIRM}
            onConfirm={({ email: confirmedEmail }) =>
              handleEmailConfirmation(confirmedEmail)
            }
            onCancel={handleCancel}
            isLoading={isLoadingRequest}
          />

          <ExistingAccountConfirmModal
            currentRole={teacherRole?.label}
            isOpen={openedModal === ModalType.EXISTING_ACCOUNT_CONFIRM}
            onConfirm={() =>
              handleEmailConfirmation(
                savedEmailForConfirmation.current || '',
                true,
              )
            }
            onCancel={handleCancel}
            isLoading={isLoadingRequest}
          />

          <DeactivateConfirmModal
            isOpen={openedModal === ModalType.DEACTIVATE_CONFIRM}
            onConfirm={() => {
              const data = getValues();
              deactivateAccount({
                email: initialValues?.email || '',
                identification: data.identification,
                identificationTypeId: data.identificationTypeId,
                countryId: data.countryId,
              });
            }}
            onCancel={handleCancel}
            isLoading={isLoadingRequest}
          />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col xs={12} md={4} className="mb-3">
                <Select
                  name="identificationType"
                  label={t(`${prefix}.identificationtype`)}
                  value={getSelectedOption(
                    identificationType,
                    identificationTypeSelect,
                  )}
                  options={identificationTypeSelect}
                  disabled={openedModal === ModalType.NONE}
                  rules={{ validate: { notEmpty: validateTextNotEmpty } }}
                />
              </Col>

              {identificationType === IdentificationDocumentCode.Passport &&
                country && (
                  <Col xs={12} md={4} className="mb-3">
                    <Select
                      name="country"
                      label={t(`${prefix}.country`)}
                      value={getSelectedOption(country, countryOptions)}
                      options={countryOptions}
                      disabled={openedModal === ModalType.NONE}
                    />
                  </Col>
                )}

              <Col xs={12} md={4} className="mb-3">
                <TextInput
                  name="identification"
                  label={t(`${prefix}.identification`)}
                  control={control}
                  readOnly={openedModal === ModalType.NONE}
                  rules={{
                    validate: {
                      notEmpty: validateTextNotEmpty,
                      format: dynamicValidate,
                    },
                  }}
                />
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <TextInput
                  name="names"
                  label={t(`${prefix}.names`)}
                  control={control}
                  disabled={!enableAllFields && openedModal === ModalType.NONE}
                  rules={{ validate: { notEmpty: validateTextNotEmpty } }}
                />
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <TextInput
                  name="paternalLastName"
                  label={t(`${prefix}.paternalLastName`)}
                  control={control}
                  disabled={!enableAllFields && openedModal === ModalType.NONE}
                  rules={{ validate: { notEmpty: validateTextNotEmpty } }}
                />
              </Col>

              <Col xs={12} md={4} className="mb-3">
                <TextInput
                  name="maternalLastName"
                  label={t(`${prefix}.maternalLastName`)}
                  control={control}
                  disabled={!enableAllFields && openedModal === ModalType.NONE}
                  rules={{ validate: { notEmpty: validateTextNotEmpty } }}
                />
              </Col>
              <Col md={4} offset={8}>
                <DateInput
                  name="birthday"
                  label={t(`${prefix}.birthday`)}
                  control={control}
                  rules={{
                    required: msgValidations.required,
                  }}
                  disabled={!enableAllFields}
                />
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <TextInput
                  name="personalEmail"
                  label={t(`${prefix}.personalEmail`)}
                  control={control}
                  disabled={!enableAllFields && openedModal === ModalType.NONE}
                />
              </Col>
              <Col xs={12} md={4}>
                <TextInput
                  name="email"
                  label={t(`${prefix}.email`)}
                  control={control}
                  tooltip={t(`${prefix}.emailTooltip`)}
                  rules={{ validate: { notEmpty: validateTextNotEmpty } }}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  name="campus"
                  label={t(`${prefix}.campus`)}
                  control={control}
                  options={campusesOptions}
                  isClearable={false}
                  rules={{
                    required: msgValidations.required,
                  }}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  name="school"
                  label={t(`${prefix}.school`)}
                  control={control}
                  options={schoolsOptions}
                  isClearable={false}
                  rules={{
                    required: msgValidations.required,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4}>
                <Select
                  name="teacherRole"
                  label={t(`${prefix}.teacherRole`)}
                  control={control}
                  options={teacherRolesOptions}
                  isClearable={false}
                  rules={{
                    required: msgValidations.required,
                  }}
                />
              </Col>
            </Row>
            <div
              className="d-flex flex-wrap justify-content-end mt-5"
              style={{ gap: 30 }}
            >
              {/* DESACTIVAR CUENTA */}
              {canDeactivateAccount && (
                <Button
                  color="danger"
                  text={t(`${prefix}.deactivate`)}
                  outlined
                  style={{ width: isMobile ? '100%' : 280 }}
                  onClick={() => setOpenedModal(ModalType.DEACTIVATE_CONFIRM)}
                />
              )}

              {/* Actualizar datos de la cuenta */}
              {canDeactivateAccount && (
                <Button
                  type="submit"
                  text={t(`${prefix}.updateData`)}
                  outlined
                  style={{ width: isMobile ? '100%' : 280 }}
                  onClick={() => setExecutingAction(SubmitAction.UPDATE_DATA)}
                  loading={isLoadingRequest}
                />
              )}

              {/* ENVIAR EMAIL DE RECUPERACION */}
              {canRecoverPassword && (
                <Button
                  type="submit"
                  text={t(`${prefix}.recoverPassword`)}
                  style={{ width: isMobile ? '100%' : 280 }}
                  onClick={() => setExecutingAction(SubmitAction.PWD_RECOVERY)}
                />
              )}

              {/* ENVIAR EMAIL DE ACTIVACION */}
              {canActivateAccount && (
                <Button
                  type="submit"
                  text={t(`${prefix}.sendInvitation`)}
                  style={{ width: isMobile ? '100%' : 280 }}
                  onClick={() => setExecutingAction(SubmitAction.ACTIVATING)}
                />
              )}
            </div>
          </form>
          <ConfirmModal
            ref={educationalBackgroundModal}
            onConfirm={goToPersonalBackground}
          />
        </>
      )}
    />
  );
}
